/**
 * Custom tooltip content for composable report charts (bar, pie, donut).
 * Design: white rounded box, shadow; label bold; value + % in segment color; optional "per unit" line.
 * No colon between label and value.
 */
/* eslint-disable react/prop-types -- tooltip props come from Recharts; we validate via TypeScript instead */
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ReportWidgetItem } from '../../model/types';
import type { TooltipProps } from 'recharts';

type RechartsTooltipPayload = TooltipProps<number, string>['payload'];

interface TooltipPointPayload {
    name?: string;
    value?: number;
    id?: string;
    color?: string;
}

export interface ReportWidgetTooltipContentProps {
    active?: boolean;
    payload?: RechartsTooltipPayload;
    label?: string;
    tooltipFormatter: (value: number) => string;
    total: number;
    items: ReportWidgetItem[];
}

const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function coerceToNumber(value: number | string | null | undefined): number {
    if (typeof value === 'number') return value;
    const parsed = Number.parseFloat(String(value ?? '0'));
    return Number.isNaN(parsed) ? 0 : parsed;
}

export function ReportWidgetTooltipContent({
    active,
    payload,
    label,
    tooltipFormatter,
    total: chartTotal,
    items: chartItems,
}: Readonly<ReportWidgetTooltipContentProps>) {
    const { t } = useTranslation();

    if (!active || !payload?.length) {
        return null;
    }

    // This is all about pulling data from the Recharts tooltip payload to display the right values
    // Lots of fallbacks as this is a generic component that can be used for multiple chart types
    const entry = payload[0];
    const value = coerceToNumber(entry.value as number | string | null);
    const pointPayload = entry.payload as TooltipPointPayload | undefined;
    const name = String(pointPayload?.name ?? entry.name ?? label ?? '');
    const segmentColor = pointPayload?.color ?? entry.color ?? undefined;

    // For displaying the value, if we were passed a total we use it to add a percentage to the value display
    const formattedValue = tooltipFormatter(value);
    const percent =
        chartTotal > 0 ? ((value / chartTotal) * 100).toFixed(1) : undefined;
    const valueLine = percent
        ? `${formattedValue} (${percent}%)`
        : formattedValue;

    // Find the item in the chartItems array that matches the id from the tooltip payload
    // This is used to get the savedPerUnit value for the tooltip (if it exists)
    const chartItem = pointPayload?.id
        ? chartItems.find((item) => item.id === pointPayload?.id)
        : undefined;
    const savedPerUnit =
        chartItem?.secondaryValues &&
        'savedPerUnit' in chartItem.secondaryValues
            ? chartItem.secondaryValues.savedPerUnit
            : undefined;
    const perUnitNum = coerceToNumber(savedPerUnit);
    const showPerUnit =
        savedPerUnit !== undefined && savedPerUnit !== '' && perUnitNum > 0;

    return (
        <Box
            data-testclass="report-widget-chart-tooltip"
            sx={{
                backgroundColor: 'background.paper',
                borderRadius: '8px',
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
                border: '1px solid',
                borderColor: 'divider',
                padding: 1.5,
                minWidth: 160,
            }}
        >
            <Typography
                component="div"
                variant="body2"
                sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    marginBottom: 0.5,
                }}
            >
                {name}
            </Typography>
            <Typography
                component="div"
                variant="body2"
                sx={{
                    color: segmentColor ?? 'text.secondary',
                    fontSize: '0.875rem',
                }}
            >
                {valueLine}
            </Typography>
            {showPerUnit && (
                <Typography
                    component="div"
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        fontSize: '0.8125rem',
                        marginTop: 0.25,
                    }}
                >
                    {currencyFormatter.format(perUnitNum)}{' '}
                    {t('composableReportWidget.tooltipPerUnit')}
                </Typography>
            )}
        </Box>
    );
}

export interface ReportWidgetChartTooltipProps
    extends TooltipProps<number, string> {
    tooltipFormatter: (value: number) => string;
    total: number;
    items: ReportWidgetItem[];
}

/**
 * Recharts Tooltip content wrapper. Pass as <Tooltip content={<ReportWidgetChartTooltip ... />} />;
 * Recharts injects active, payload, label when rendering.
 */
export function ReportWidgetChartTooltip({
    active,
    payload,
    label,
    tooltipFormatter,
    total,
    items,
}: Readonly<ReportWidgetChartTooltipProps>) {
    return (
        <ReportWidgetTooltipContent
            active={active}
            payload={payload}
            label={label}
            tooltipFormatter={tooltipFormatter}
            total={total}
            items={items}
        />
    );
}
